import type {
  TreeRuntimeLifecycleAttributionStatus,
  TreeRuntimeLifecycleBrowserOwnerMode,
  TreeRuntimeMissingParentLifecycleAttributionDecision,
} from './TreeRuntimeMissingParentLifecycleAttributionParity';

export const TREE_RUNTIME_MISSING_PARENT_LIFECYCLE_ATTRIBUTION_EVIDENCE_SOURCE =
  'TreeRuntimeMissingParentLifecycleAttributionEvidence' as const;

export const TREE_RUNTIME_MISSING_PARENT_LIFECYCLE_ATTRIBUTION_PUBLICATION_MILESTONE =
  'CC-TREE-LIVE-003G2N' as const;

export type TreeRuntimeLifecyclePublicationSharedSeverity =
  | 'info'
  | 'warn'
  | 'fail';

export type TreeRuntimeLifecyclePublicationRuntimeSeverity =
  | 'info'
  | 'warning'
  | 'error';

export type TreeRuntimeLifecyclePublicationEvent =
  | 'TREE_RUNTIME_MISSING_PARENT_LIFECYCLE_ATTRIBUTED'
  | 'TREE_RUNTIME_MISSING_PARENT_LIFECYCLE_WITHHELD_OWNER_INCOMPATIBLE'
  | 'TREE_RUNTIME_MISSING_PARENT_LIFECYCLE_WITHHELD_COUNT_MISMATCH'
  | 'TREE_RUNTIME_MISSING_PARENT_LIFECYCLE_WITHHELD_INVALID_SERVER_RESPONSE'
  | 'TREE_RUNTIME_MISSING_PARENT_LIFECYCLE_REJECTED_INVALID_BROWSER_COUNT'
  | 'TREE_RUNTIME_MISSING_PARENT_LIFECYCLE_REJECTED_SERVER_AUTHORITY_VIOLATION';

export interface TreeRuntimeMissingParentLifecycleAttributionPublicationProjection {
  readonly milestone:
    typeof TREE_RUNTIME_MISSING_PARENT_LIFECYCLE_ATTRIBUTION_PUBLICATION_MILESTONE;

  readonly source:
    typeof TREE_RUNTIME_MISSING_PARENT_LIFECYCLE_ATTRIBUTION_EVIDENCE_SOURCE;

  readonly event:
    TreeRuntimeLifecyclePublicationEvent;

  readonly attributionStatus:
    TreeRuntimeLifecycleAttributionStatus;

  readonly browserOwnerResolutionMode:
    TreeRuntimeLifecycleBrowserOwnerMode;

  readonly sharedSeverity:
    TreeRuntimeLifecyclePublicationSharedSeverity;

  readonly runtimeSeverity:
    TreeRuntimeLifecyclePublicationRuntimeSeverity;

  readonly summary: string;

  readonly evidence:
    readonly string[];

  readonly metadata:
    Readonly<Record<string, unknown>>;

  readonly fingerprint: string;

  readonly lifecycleCountsPublished: boolean;
  readonly identityParityProven: false;
  readonly readOnly: true;
  readonly repairExecuted: false;
  readonly repairReady: false;
}

const DECISION_KEYS =
  Object.freeze([
    'status',
    'readOnly',
    'repairExecuted',
    'identityParityProven',
    'aggregateCountParityProven',
    'lifecycleAttributionEligible',
    'browserOwnerResolutionMode',
    'browserMissingReferenceParentIdsCount',
    'serverMissingReferenceParentIdsCount',
    'missingParentReferenceLifecycle',
  ] as const);

const LIFECYCLE_KEYS =
  Object.freeze([
    'missingParentReferenceActiveSameGraphCount',
    'missingParentReferenceSoftDeletedSameGraphCount',
    'missingParentReferenceCrossGraphCount',
    'missingParentReferencePhysicallyAbsentCount',
    'missingParentReferenceIndeterminateCount',
  ] as const);

const isRecord =
  (
    value: unknown,
  ): value is Record<string, unknown> =>
    !!value &&
    typeof value === 'object' &&
    !Array.isArray(value);

const exactKeys =
  (
    value: Record<string, unknown>,
    expected: readonly string[],
  ): boolean => {
    const actual =
      Object.keys(value).sort();

    const required =
      [...expected].sort();

    return (
      actual.length === required.length &&
      actual.every(
        (key, index) =>
          key === required[index],
      )
    );
  };

const validBrowserCount =
  (
    value: unknown,
  ): value is number =>
    Number.isInteger(value) &&
    Number.isSafeInteger(value) &&
    (value as number) >= 0 &&
    (value as number) <= 1_000_000;

const validServerCount =
  (
    value: unknown,
  ): value is number =>
    Number.isSafeInteger(value) &&
    (value as number) >= 0;

const validStatus =
  (
    value: unknown,
  ): value is TreeRuntimeLifecycleAttributionStatus =>
    value === 'ATTRIBUTED' ||
    value === 'INVALID_BROWSER_COHORT_COUNT' ||
    value === 'OWNER_RESOLUTION_INCOMPATIBLE' ||
    value === 'INVALID_SERVER_PROBE_RESPONSE' ||
    value === 'SERVER_LIFECYCLE_AUTHORITY_VIOLATION' ||
    value === 'AGGREGATE_COUNT_MISMATCH';

const validOwnerMode =
  (
    value: unknown,
  ): value is TreeRuntimeLifecycleBrowserOwnerMode =>
    value === 'ACTIVE_CLAIM_SINGLE_OWNER' ||
    value === 'SELF_FALLBACK' ||
    value === 'INVITER_PROTECTION' ||
    value === 'MULTI_CLAIM_AMBIGUOUS' ||
    value === 'INDETERMINATE';

const compatibleOwnerMode =
  (
    value:
      TreeRuntimeLifecycleBrowserOwnerMode,
  ): boolean =>
    value === 'ACTIVE_CLAIM_SINGLE_OWNER' ||
    value === 'SELF_FALLBACK';

export const sanitizeTreeRuntimeMissingParentLifecycleAttributionForPublication =
  (
    candidate: unknown,
  ): TreeRuntimeMissingParentLifecycleAttributionDecision | null => {
    if (!isRecord(candidate)) {
      return null;
    }

    if (
      !exactKeys(
        candidate,
        DECISION_KEYS,
      ) ||
      !validStatus(candidate.status) ||
      !validOwnerMode(
        candidate.browserOwnerResolutionMode,
      ) ||
      candidate.readOnly !== true ||
      candidate.repairExecuted !== false ||
      candidate.identityParityProven !== false
    ) {
      return null;
    }

    const status =
      candidate.status;

    const mode =
      candidate.browserOwnerResolutionMode;

    const browserCount =
      candidate.browserMissingReferenceParentIdsCount;

    const serverCount =
      candidate.serverMissingReferenceParentIdsCount;

    if (status === 'ATTRIBUTED') {
      const lifecycle =
        candidate.missingParentReferenceLifecycle;

      if (
        !compatibleOwnerMode(mode) ||
        candidate.aggregateCountParityProven !== true ||
        candidate.lifecycleAttributionEligible !== true ||
        !validBrowserCount(browserCount) ||
        !validServerCount(serverCount) ||
        browserCount !== serverCount ||
        !isRecord(lifecycle) ||
        !exactKeys(
          lifecycle,
          LIFECYCLE_KEYS,
        )
      ) {
        return null;
      }

      const activeSameGraphCount =
        lifecycle
          .missingParentReferenceActiveSameGraphCount;

      const softDeletedSameGraphCount =
        lifecycle
          .missingParentReferenceSoftDeletedSameGraphCount;

      const crossGraphCount =
        lifecycle
          .missingParentReferenceCrossGraphCount;

      const physicallyAbsentCount =
        lifecycle
          .missingParentReferencePhysicallyAbsentCount;

      const indeterminateCount =
        lifecycle
          .missingParentReferenceIndeterminateCount;

      if (
        !validBrowserCount(
          activeSameGraphCount,
        ) ||
        !validBrowserCount(
          softDeletedSameGraphCount,
        ) ||
        !validBrowserCount(
          crossGraphCount,
        ) ||
        !validBrowserCount(
          physicallyAbsentCount,
        ) ||
        !validBrowserCount(
          indeterminateCount,
        )
      ) {
        return null;
      }

      if (
        activeSameGraphCount !== 0 ||
        crossGraphCount !== 0 ||
        physicallyAbsentCount !== 0 ||
        softDeletedSameGraphCount +
          indeterminateCount !==
          serverCount
      ) {
        return null;
      }

      return candidate as unknown as
        TreeRuntimeMissingParentLifecycleAttributionDecision;
    }

    if (
      candidate.aggregateCountParityProven !== false ||
      candidate.lifecycleAttributionEligible !== false ||
      candidate.missingParentReferenceLifecycle !== null
    ) {
      return null;
    }

    if (
      status ===
        'INVALID_BROWSER_COHORT_COUNT'
    ) {
      return (
        browserCount === null &&
        serverCount === null
      )
        ? candidate as unknown as
            TreeRuntimeMissingParentLifecycleAttributionDecision
        : null;
    }

    if (
      !validBrowserCount(browserCount)
    ) {
      return null;
    }

    if (
      status ===
        'OWNER_RESOLUTION_INCOMPATIBLE'
    ) {
      return (
        !compatibleOwnerMode(mode) &&
        serverCount === null
      )
        ? candidate as unknown as
            TreeRuntimeMissingParentLifecycleAttributionDecision
        : null;
    }

    if (
      status ===
        'INVALID_SERVER_PROBE_RESPONSE' ||
      status ===
        'SERVER_LIFECYCLE_AUTHORITY_VIOLATION'
    ) {
      return (
        compatibleOwnerMode(mode) &&
        serverCount === null
      )
        ? candidate as unknown as
            TreeRuntimeMissingParentLifecycleAttributionDecision
        : null;
    }

    if (
      status ===
        'AGGREGATE_COUNT_MISMATCH'
    ) {
      return (
        compatibleOwnerMode(mode) &&
        validServerCount(serverCount) &&
        serverCount !== browserCount
      )
        ? candidate as unknown as
            TreeRuntimeMissingParentLifecycleAttributionDecision
        : null;
    }

    return null;
  };

export const createTreeRuntimeMissingParentLifecycleAttributionPublicationFingerprint =
  (
    decision:
      TreeRuntimeMissingParentLifecycleAttributionDecision,
  ): string => {
    const lifecycle =
      decision
        .missingParentReferenceLifecycle;

    const canonical =
      JSON.stringify([
        decision.status,
        decision.browserOwnerResolutionMode,
        decision.browserMissingReferenceParentIdsCount,
        decision.serverMissingReferenceParentIdsCount,
        decision.aggregateCountParityProven,
        decision.lifecycleAttributionEligible,
        false,
        lifecycle
          ?.missingParentReferenceActiveSameGraphCount ??
          null,
        lifecycle
          ?.missingParentReferenceSoftDeletedSameGraphCount ??
          null,
        lifecycle
          ?.missingParentReferenceCrossGraphCount ??
          null,
        lifecycle
          ?.missingParentReferencePhysicallyAbsentCount ??
          null,
        lifecycle
          ?.missingParentReferenceIndeterminateCount ??
          null,
      ]);

    return `003g2n:${canonical}`;
  };

const eventForStatus =
  (
    status:
      TreeRuntimeLifecycleAttributionStatus,
  ): TreeRuntimeLifecyclePublicationEvent => {
    switch (status) {
      case 'ATTRIBUTED':
        return 'TREE_RUNTIME_MISSING_PARENT_LIFECYCLE_ATTRIBUTED';

      case 'OWNER_RESOLUTION_INCOMPATIBLE':
        return 'TREE_RUNTIME_MISSING_PARENT_LIFECYCLE_WITHHELD_OWNER_INCOMPATIBLE';

      case 'AGGREGATE_COUNT_MISMATCH':
        return 'TREE_RUNTIME_MISSING_PARENT_LIFECYCLE_WITHHELD_COUNT_MISMATCH';

      case 'INVALID_SERVER_PROBE_RESPONSE':
        return 'TREE_RUNTIME_MISSING_PARENT_LIFECYCLE_WITHHELD_INVALID_SERVER_RESPONSE';

      case 'INVALID_BROWSER_COHORT_COUNT':
        return 'TREE_RUNTIME_MISSING_PARENT_LIFECYCLE_REJECTED_INVALID_BROWSER_COUNT';

      case 'SERVER_LIFECYCLE_AUTHORITY_VIOLATION':
        return 'TREE_RUNTIME_MISSING_PARENT_LIFECYCLE_REJECTED_SERVER_AUTHORITY_VIOLATION';
    }
  };

const severityForStatus =
  (
    status:
      TreeRuntimeLifecycleAttributionStatus,
  ): readonly [
    TreeRuntimeLifecyclePublicationSharedSeverity,
    TreeRuntimeLifecyclePublicationRuntimeSeverity,
  ] => {
    if (
      status ===
        'INVALID_BROWSER_COHORT_COUNT' ||
      status ===
        'SERVER_LIFECYCLE_AUTHORITY_VIOLATION'
    ) {
      return [
        'fail',
        'error',
      ] as const;
    }

    if (
      status ===
        'AGGREGATE_COUNT_MISMATCH' ||
      status ===
        'INVALID_SERVER_PROBE_RESPONSE'
    ) {
      return [
        'warn',
        'warning',
      ] as const;
    }

    return [
      'info',
      'info',
    ] as const;
  };

export const projectTreeRuntimeMissingParentLifecycleAttributionPublication =
  (
    candidate: unknown,
  ): TreeRuntimeMissingParentLifecycleAttributionPublicationProjection | null => {
    const decision =
      sanitizeTreeRuntimeMissingParentLifecycleAttributionForPublication(
        candidate,
      );

    if (!decision) {
      return null;
    }

    const [
      sharedSeverity,
      runtimeSeverity,
    ] =
      severityForStatus(
        decision.status,
      );

    const lifecycle =
      decision
        .missingParentReferenceLifecycle;

    const lifecycleCountsPublished =
      decision.status === 'ATTRIBUTED' &&
      lifecycle !== null;

    const evidence =
      [
        `milestone=${TREE_RUNTIME_MISSING_PARENT_LIFECYCLE_ATTRIBUTION_PUBLICATION_MILESTONE}`,
        `attributionStatus=${decision.status}`,
        `browserOwnerResolutionMode=${decision.browserOwnerResolutionMode}`,
        `aggregateCountParityProven=${decision.aggregateCountParityProven}`,
        `lifecycleAttributionEligible=${decision.lifecycleAttributionEligible}`,
        'identityParityProven=false',
        `lifecycleCountsPublished=${lifecycleCountsPublished}`,
        'readOnly=true',
        'repairExecuted=false',
      ];

    const metadata:
      Record<string, unknown> = {
        milestone:
          TREE_RUNTIME_MISSING_PARENT_LIFECYCLE_ATTRIBUTION_PUBLICATION_MILESTONE,
        attributionStatus:
          decision.status,
        browserOwnerResolutionMode:
          decision.browserOwnerResolutionMode,
        aggregateCountParityProven:
          decision.aggregateCountParityProven,
        lifecycleAttributionEligible:
          decision.lifecycleAttributionEligible,
        identityParityProven: false,
        lifecycleCountsPublished,
        readOnly: true,
        repairExecuted: false,
        repairReady: false,
      };

    if (
      lifecycleCountsPublished &&
      lifecycle
    ) {
      evidence.push(
        `browserMissingReferenceParentIdsCount=${decision.browserMissingReferenceParentIdsCount}`,
        `serverMissingReferenceParentIdsCount=${decision.serverMissingReferenceParentIdsCount}`,
        `softDeletedSameGraphCount=${lifecycle.missingParentReferenceSoftDeletedSameGraphCount}`,
        `indeterminateCount=${lifecycle.missingParentReferenceIndeterminateCount}`,
        'activeSameGraphCount=0',
        'crossGraphCount=0',
        'physicallyAbsentCount=0',
      );

      Object.assign(
        metadata,
        {
          browserMissingReferenceParentIdsCount:
            decision.browserMissingReferenceParentIdsCount,
          serverMissingReferenceParentIdsCount:
            decision.serverMissingReferenceParentIdsCount,
          softDeletedSameGraphCount:
            lifecycle
              .missingParentReferenceSoftDeletedSameGraphCount,
          indeterminateCount:
            lifecycle
              .missingParentReferenceIndeterminateCount,
          activeSameGraphCount: 0,
          crossGraphCount: 0,
          physicallyAbsentCount: 0,
        },
      );
    }

    const summary =
      lifecycleCountsPublished
        ? `Tree missing-parent lifecycle attribution qualified for aggregate runtime publication with ${decision.serverMissingReferenceParentIdsCount} occurrence(s).`
        : `Tree missing-parent lifecycle attribution observed ${decision.status}; lifecycle counts withheld.`;

    return Object.freeze({
      milestone:
        TREE_RUNTIME_MISSING_PARENT_LIFECYCLE_ATTRIBUTION_PUBLICATION_MILESTONE,
      source:
        TREE_RUNTIME_MISSING_PARENT_LIFECYCLE_ATTRIBUTION_EVIDENCE_SOURCE,
      event:
        eventForStatus(
          decision.status,
        ),
      attributionStatus:
        decision.status,
      browserOwnerResolutionMode:
        decision.browserOwnerResolutionMode,
      sharedSeverity,
      runtimeSeverity,
      summary,
      evidence:
        Object.freeze([
          ...evidence,
        ]),
      metadata:
        Object.freeze({
          ...metadata,
        }),
      fingerprint:
        createTreeRuntimeMissingParentLifecycleAttributionPublicationFingerprint(
          decision,
        ),
      lifecycleCountsPublished,
      identityParityProven: false,
      readOnly: true,
      repairExecuted: false,
      repairReady: false,
    });
  };
